import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { AngularFirestore, AngularFirestoreDocument } from '@angular/fire/compat/firestore';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore'; // Required for side effects like serverTimestamp
import { Observable, of } from 'rxjs';
import { map, catchError, first } from 'rxjs/operators';

@Component({
    selector: 'app-prompt-generator',
    templateUrl: './prompt-generator.component.html',
    styleUrls: ['./prompt-generator.component.scss']
})
export class PromptGeneratorComponent implements OnInit {

    topic: string = '';
    generatedPrompts: string[] = [];
    isLoading: boolean = false;
    error: string | null = null;

    // Properties for prompt management
    managedPrompts: ManagedPrompt[] = [];
    newPromptText: string = '';
    newPromptCategory: string = '';
    editingPrompt: ManagedPrompt | null = null;
    availableCategories: string[] = [];

    // Track the currently selected prompt for viewing/editing sub-prompts in the tree
    selectedPrompt: ManagedPrompt | null = null;

    // Modal dialog control
    showPromptDialog: boolean = false;
    currentParentForNewPrompt: ManagedPrompt | null = null; // Used when creating a new sub-prompt directly

    // New property for the quick add form for sub-prompts to the selected prompt
    newSubPromptTextForSelected: string = '';

    private promptsCollection = 'managedPrompts';

    constructor(private http: HttpClient, private afs: AngularFirestore) { }

    ngOnInit(): void {
        this.loadManagedPrompts();
    }

    // Method to toggle the expanded state of a prompt in the tree
    toggleExpand(prompt: ManagedPrompt): void {
        prompt.isExpanded = !prompt.isExpanded;
        // Note: If prompts are re-fetched or re-bound, this state might be lost
        // unless it's persisted or managed carefully in the data loading/binding.
        // For now, this handles local UI state.
    }

    generatePrompts(): void {
        if (!this.topic.trim()) {
            console.warn('Topic cannot be empty');
            this.generatedPrompts = [];
            this.error = 'Topic cannot be empty';
            return;
        }

        this.isLoading = true;
        this.error = null;
        this.generatedPrompts = [];

        const firebaseUrl = 'https://us-central1-xbuild3d.cloudfunctions.net/generateSubPrompts';
        const payload = {
            userInput: this.topic,
            aiProvider: 'openai'
        };

        this.http.post<any>(firebaseUrl, payload).subscribe({
            next: (response) => {
                if (response && Array.isArray(response.subProjects)) {
                    this.generatedPrompts = response.subProjects.map((subProject: any) => subProject.prompt);
                    console.log('Generated sub-prompts:', this.generatedPrompts);

                    // If a main prompt is selected, add these as sub-prompts
                    if (this.selectedPrompt && this.selectedPrompt.id) {
                        this.addGeneratedPromptsToSelected();
                    }
                } else {
                    console.error('Invalid response structure from generateSubPrompts:', response);
                    this.error = 'Invalid response from server (expected subProjects array).';
                    this.generatedPrompts = ['Error: Could not fetch prompts due to invalid server response.'];
                }
                this.isLoading = false;
            },
            error: (err: HttpErrorResponse) => {
                console.error('Error generating sub-prompts:', err);
                this.error = `Error: ${err.error?.details || err.error?.error || err.message || 'Could not fetch prompts from server.'}`;
                this.generatedPrompts = [`Failed to generate prompts. ${this.error}`];
                this.isLoading = false;
            }
        });

        console.log(`Requesting sub-prompts for topic: ${this.topic} using ${payload.aiProvider}`);
    }

    // --- Prompt Management Functions ---

    loadManagedPrompts(): void {
        this.isLoading = true;
        this.error = null;
        console.log('Loading managed prompts from Firestore...');

        this.afs.collection<ManagedPrompt>(this.promptsCollection)
            .snapshotChanges()
            .pipe(
                map(actions => actions.map(a => {
                    const data = a.payload.doc.data() as ManagedPrompt;
                    const id = a.payload.doc.id;
                    // Initialize isExpanded for all prompts and sub-prompts
                    const initializeExpansion = (p: ManagedPrompt): ManagedPrompt => {
                        p.isExpanded = p.isExpanded || false; // Default to false if not set
                        if (p.subPrompts) {
                            p.subPrompts = p.subPrompts.map(initializeExpansion);
                        }
                        return p;
                    };
                    return initializeExpansion({ id, ...data });
                })),
                catchError(err => {
                    console.error('Error loading prompts from Firestore:', err);
                    this.error = `Error loading prompts: ${err.message}`;
                    this.isLoading = false;
                    this.managedPrompts = []; // Clear or use placeholder
                    return of([]); // Return an empty array on error
                })
            )
            .subscribe(prompts => {
                this.managedPrompts = prompts;
                this.updateAvailableCategories();
                this.isLoading = false;
                console.log('Managed prompts loaded/updated:', this.managedPrompts);
            }, err => { // Additional error handling for the subscription itself
                console.error('Subscription error loading prompts:', err);
                this.error = `Subscription error: ${err.message}`;
                this.isLoading = false;
            });
    }

    async saveManagedPrompt(): Promise<void> {
        if (!this.newPromptText.trim()) {
            this.error = 'Prompt text cannot be empty.';
            return;
        }
        // Category can be optional for sub-prompts, will inherit from parent if empty.
        if (!this.editingPrompt && !this.currentParentForNewPrompt && !this.newPromptCategory.trim()) {
            this.error = 'Category cannot be empty for a new top-level prompt.';
            return;
        }

        this.error = null;
        this.isLoading = true;

        const promptData: Partial<ManagedPrompt> = {
            text: this.newPromptText,
            category: this.newPromptCategory, // Will be refined below
        };

        try {
            if (this.editingPrompt) { // Editing existing prompt (top-level or sub-level)
                promptData.category = this.newPromptCategory || this.editingPrompt.category; // Keep original if new is empty

                if (this.editingPrompt.parentId) { // Editing a sub-prompt
                    const parentId = this.editingPrompt.parentId;
                    const parentDocRef = this.afs.doc<ManagedPrompt>(`${this.promptsCollection}/${parentId}`);
                    const parentSnapshot = await parentDocRef.get().pipe(first()).toPromise();
                    if (!parentSnapshot || !parentSnapshot.exists) throw new Error("Parent prompt not found for sub-prompt update!");
                    let parentData = parentSnapshot.data() as ManagedPrompt;

                    parentData.subPrompts = parentData.subPrompts?.map(sp =>
                        sp.id === this.editingPrompt!.id ? { ...sp, text: promptData.text!, category: promptData.category! } : sp
                    ) || [];

                    await parentDocRef.update({ subPrompts: parentData.subPrompts });
                    console.log('Updated sub-prompt:', this.editingPrompt.id, 'in parent:', parentId);
                } else { // Editing a top-level prompt
                    await this.afs.doc(`${this.promptsCollection}/${this.editingPrompt.id}`).update(promptData);
                    console.log('Updated top-level prompt:', this.editingPrompt.id);
                }
            } else if (this.currentParentForNewPrompt) { // Adding new sub-prompt
                promptData.category = this.newPromptCategory || this.currentParentForNewPrompt.category; // Inherit if empty
                const newSubPrompt: ManagedPrompt = {
                    id: this.afs.createId(), // Firestore generated ID for better uniqueness
                    text: promptData.text!,
                    category: promptData.category!,
                    parentId: this.currentParentForNewPrompt.id!,
                    subPrompts: [],
                    isExpanded: false
                };

                const parentDocRef = this.afs.doc<ManagedPrompt>(`${this.promptsCollection}/${this.currentParentForNewPrompt.id}`);
                const parentSnapshot = await parentDocRef.get().pipe(first()).toPromise();
                if (!parentSnapshot || !parentSnapshot.exists) throw new Error("Parent prompt not found for adding new sub-prompt!");
                let parentData = parentSnapshot.data() as ManagedPrompt;

                parentData.subPrompts = [...(parentData.subPrompts || []), newSubPrompt];
                await parentDocRef.update({ subPrompts: parentData.subPrompts });
                console.log('Added new sub-prompt to parent:', this.currentParentForNewPrompt.id);

            } else { // Adding new top-level prompt
                if (!promptData.category) { // Should have been caught by validation, but as a safeguard
                    this.error = "Category is required for new top-level prompts.";
                    this.isLoading = false;
                    return;
                }
                const newPrompt: ManagedPrompt = {
                    id: this.afs.createId(), // Will be overwritten by add, but good for consistency
                    text: promptData.text!,
                    category: promptData.category!,
                    subPrompts: [], // Initialize with empty subPrompts
                    isExpanded: false
                    // parentId is undefined for top-level prompts
                };
                const docRef = await this.afs.collection(this.promptsCollection).add(newPrompt);
                console.log('Saved new top-level prompt with ID:', docRef.id);
            }

            // Successfully saved, now refresh relevant parts of the tree or selected item
            if (this.editingPrompt) {
                // If a sub-prompt was edited, we might need to refresh its parent in the local tree
                // or re-fetch the specific parent to get the updated subPrompts array.
                // For simplicity, loadManagedPrompts will refresh the whole tree.
            } else if (this.currentParentForNewPrompt) {
                // If a new sub-prompt was added, the parent's subPrompts array has changed.
            }
            // Full reload for now, can be optimized later if performance issues arise
            this.loadManagedPrompts();

            this.closePromptDialog(); // Resets form and currentParentForNewPrompt
        } catch (err: any) {
            console.error('Error saving prompt:', err);
            this.error = `Error saving prompt: ${err.message || 'Unknown error'}`;
        } finally {
            this.isLoading = false;
        }
    }

    editManagedPrompt(prompt: ManagedPrompt, parent?: ManagedPrompt): void {
        this.openPromptDialog(prompt);
    }

    async deleteManagedPrompt(promptId: string, parentId?: string): Promise<void> {
        console.log('Attempting to delete prompt:', promptId, 'from parent:', parentId);
        this.isLoading = true;
        this.error = null;
        try {
            if (parentId) {
                const parentDocRef: AngularFirestoreDocument<ManagedPrompt> = this.afs.doc<ManagedPrompt>(`${this.promptsCollection}/${parentId}`);
                const parentSnapshot = await parentDocRef.get().pipe(first()).toPromise();

                if (!parentSnapshot || !parentSnapshot.exists) {
                    console.warn("Parent prompt not found for sub-prompt deletion! ID:", parentId);
                    // Potentially the parent was deleted, or this is an orphaned sub-prompt reference.
                    // Decide if we should proceed to remove from local managedPrompts if found.
                    // For now, we'll log and let loadManagedPrompts() eventually correct the tree.
                    this.error = "Parent prompt not found. The prompt list may be out of sync.";
                    // Attempt to remove locally to reflect in UI immediately if possible
                    this.removePromptFromLocalTree(promptId, parentId);
                    this.isLoading = false;
                    return; // Avoid further Firestore operations if parent is gone
                }
                let parentData = parentSnapshot.data() as ManagedPrompt;
                if (parentData.subPrompts) {
                    const initialCount = parentData.subPrompts.length;
                    parentData.subPrompts = parentData.subPrompts.filter(sp => sp.id !== promptId);
                    if (parentData.subPrompts.length < initialCount) {
                        await parentDocRef.update({ subPrompts: parentData.subPrompts });
                        console.log('Sub-prompt deleted:', promptId, 'from parent:', parentId);
                    } else {
                        console.warn('Sub-prompt ID not found in parent\'s subPrompts during deletion:', promptId);
                    }
                }
            } else {
                await this.afs.doc(`${this.promptsCollection}/${promptId}`).delete();
                console.log('Deleted main prompt:', promptId);
            }

            if (this.editingPrompt && this.editingPrompt.id === promptId) {
                this.closePromptDialog(); // also calls resetForm
            } else {
                this.resetForm(); // Ensure no lingering edit state
            }

            // If we deleted the currently selected prompt, clear the selection
            if (this.selectedPrompt && this.selectedPrompt.id === promptId) {
                this.selectedPrompt = null;
            }
            // If we deleted a sub-prompt of the selected prompt, refresh the selected prompt
            else if (this.selectedPrompt && parentId === this.selectedPrompt.id) {
                // The parent (selectedPrompt) was modified, reload it or the whole tree
                this.loadManagedPrompts(); // Reload to reflect the change in sub-prompts
            } else {
                // If a different prompt was deleted, reload the whole tree
                this.loadManagedPrompts();
            }

        } catch (err: any) {
            console.error('Error deleting prompt:', err);
            this.error = `Error deleting prompt: ${err.message || 'Unknown error'}`;
        } finally {
            this.isLoading = false;
        }
    }

    cancelEdit(): void {
        this.resetForm();
    }

    private resetForm(): void {
        this.newPromptText = '';
        this.newPromptCategory = '';
        this.editingPrompt = null;
        this.currentParentForNewPrompt = null; // Also reset this
        this.error = null;
    }

    private updateAvailableCategories(): void {
        const categories = new Set<string>();
        const collectCategories = (prompts: ManagedPrompt[]) => {
            for (const p of prompts) {
                if (p.category) categories.add(p.category);
                if (p.subPrompts) {
                    collectCategories(p.subPrompts);
                }
            }
        };
        collectCategories(this.managedPrompts);
        this.availableCategories = [...categories].sort();
    }

    // Method to select a prompt and display its sub-prompts
    selectPrompt(prompt: ManagedPrompt): void {
        // Toggle selection: if the same prompt is clicked, deselect it. Otherwise, select the new one.
        if (this.selectedPrompt && this.selectedPrompt.id === prompt.id) {
            this.selectedPrompt = null;
            console.log('Deselected prompt:', prompt.text);
        } else {
            this.selectedPrompt = prompt; // Directly assign the prompt object from the tree
            console.log('Selected prompt:', this.selectedPrompt.text);
            // No re-fetching needed here if the tree holds the full data.
            // Re-fetching (as done previously) might be safer if data can become stale quickly
            // but can also lead to loss of transient state like `isExpanded`.
            // For now, trust the local tree data for selection.
        }
        this.newSubPromptTextForSelected = ''; // Clear quick add text when selection changes
    }

    // Add a new sub-prompt to the currently selected prompt using the quick add form
    async addSubPromptToSelected(): Promise<void> {
        if (!this.selectedPrompt || !this.newSubPromptTextForSelected.trim()) {
            if (!this.selectedPrompt) this.error = "No prompt selected to add sub-prompt to.";
            if (this.newSubPromptTextForSelected && !this.newSubPromptTextForSelected.trim()) this.error = "Sub-prompt text cannot be empty.";
            return;
        }

        this.isLoading = true;
        this.error = null;

        const targetParentNode = this.selectedPrompt;

        try {
            const context = this.getPromptContext(targetParentNode);
            if (!context) {
                throw new Error("Could not determine context for the selected prompt. The local tree might be inconsistent.");
            }
            const { rootDocId, pathIdsToPrompt: pathIdsToParentNode } = context;

            const rootDocRef = this.afs.doc<ManagedPrompt>(`${this.promptsCollection}/${rootDocId}`);
            const rootDocSnapshot = await rootDocRef.get().pipe(first()).toPromise();

            if (!rootDocSnapshot || !rootDocSnapshot.exists) {
                throw new Error(`Root document ${rootDocId} not found in Firestore! This can happen if it was deleted externally.`);
            }

            let rootData = rootDocSnapshot.data() as ManagedPrompt;

            const newSubPrompt: ManagedPrompt = {
                id: this.afs.createId(),
                text: this.newSubPromptTextForSelected,
                category: targetParentNode.category, // Inherit category from its direct parent (targetParentNode)
                parentId: targetParentNode.id!,      // The new prompt's parent is the targetParentNode
                subPrompts: [],
                isExpanded: false
            };

            const updated = this.updatePromptAtPath(rootData, pathIdsToParentNode, (parentToUpdate) => {
                parentToUpdate.subPrompts = [...(parentToUpdate.subPrompts || []), newSubPrompt];
            });

            if (!updated) {
                throw new Error(`Failed to find path to selected prompt (${targetParentNode.text}) within its root document for update.`);
            }

            await rootDocRef.update(rootData); // Update the entire root document

            // A full reload is safest to ensure data consistency across the tree and reflect changes
            this.loadManagedPrompts();

            this.newSubPromptTextForSelected = ''; // Clear the input
            console.log('Added new sub-prompt via quick add:', newSubPrompt.text, 'to parent:', targetParentNode.text);
        } catch (err: any) {
            console.error('Error adding sub-prompt via quick add:', err);
            this.error = `Error adding sub-prompt: ${err.message || 'Unknown error'}`;
        } finally {
            this.isLoading = false;
        }
    }

    // Add generated prompts to the currently selected prompt
    async addGeneratedPromptsToSelected(): Promise<void> {
        if (!this.selectedPrompt || !this.generatedPrompts.length) {
            return;
        }

        try {
            // Get the latest version of the selected prompt
            const promptDocRef = this.afs.doc<ManagedPrompt>(`${this.promptsCollection}/${this.selectedPrompt.id}`);
            const promptSnapshot = await promptDocRef.get().pipe(first()).toPromise();

            if (!promptSnapshot || !promptSnapshot.exists) {
                throw new Error("Selected prompt not found for adding generated prompts!");
            }

            const promptData = promptSnapshot.data() as ManagedPrompt;
            let subPrompts = promptData.subPrompts ? [...promptData.subPrompts] : [];

            // Add generated prompts as sub-prompts
            this.generatedPrompts.forEach((promptText, index) => {
                subPrompts.push({
                    id: this.afs.createId(), // Use Firestore ID
                    text: promptText,
                    category: this.selectedPrompt!.category, // Inherit from selected parent
                    parentId: this.selectedPrompt!.id!,
                    subPrompts: [],
                    isExpanded: false
                });
            });

            // Update the document
            await promptDocRef.update({ subPrompts });

            // Refresh the selected prompt or the whole tree
            // Forcing a reload of managed prompts to correctly update the tree view
            this.loadManagedPrompts();
            // this.selectPrompt(this.selectedPrompt); // Re-selecting might be needed if not reloading all

            this.generatedPrompts = []; // Clear generated prompts after adding
            console.log('Added generated prompts as sub-prompts to selected prompt');
        } catch (err: any) {
            console.error('Error adding generated prompts as sub-prompts:', err);
            this.error = `Error adding generated prompts: ${err.message || 'Unknown error'}`;
        }
    }

    // --- Dialog Management Methods ---

    /**
     * Opens the prompt dialog for creating a new prompt or editing an existing one
     * @param promptToEdit The prompt to edit (if editing)
     * @param parentForNewSubPrompt The parent prompt IF we are creating a NEW sub-prompt directly (e.g. from a + button in the tree)
     */
    openPromptDialog(promptToEdit?: ManagedPrompt, parentForNewSubPrompt?: ManagedPrompt): void {
        this.resetForm(); // Clears editingPrompt, newPromptText, newPromptCategory, currentParentForNewPrompt

        if (promptToEdit) {
            // Editing an existing prompt (could be top-level or sub-prompt)
            this.editingPrompt = { ...promptToEdit }; // Create a copy to avoid direct mutation if dialog is cancelled
            this.newPromptText = promptToEdit.text;
            this.newPromptCategory = promptToEdit.category;
            // parentId is already part of promptToEdit if it's a sub-prompt
            // No need to set currentParentForNewPrompt here, as we are editing, not creating under a new parent.
        } else if (parentForNewSubPrompt) {
            // Creating a NEW sub-prompt under parentForNewSubPrompt
            this.currentParentForNewPrompt = parentForNewSubPrompt;
            this.newPromptCategory = parentForNewSubPrompt.category; // Pre-fill category from parent
            // editingPrompt remains null
        } else {
            // Creating a new TOP-LEVEL prompt
            // editingPrompt is null, currentParentForNewPrompt is null
            // newPromptText and newPromptCategory are already empty from resetForm
        }
        this.showPromptDialog = true;
    }

    /**
     * Closes the prompt dialog without saving
     */
    closePromptDialog(): void {
        this.showPromptDialog = false;
        this.resetForm(); // Call resetForm to ensure all relevant states are cleared
    }

    /**
     * Saves the prompt from the dialog.
     * This now acts as a simple trigger for the refactored saveManagedPrompt.
     */
    savePromptFromDialog(): void {
        // All logic is now consolidated in saveManagedPrompt.
        // openPromptDialog sets up editingPrompt or currentParentForNewPrompt.
        // newPromptText and newPromptCategory are bound from the modal form.
        this.saveManagedPrompt();
        // closePromptDialog is called within saveManagedPrompt upon success.
    }

    // Helper function to recursively remove a prompt from the local tree (used in delete if parent not found)
    private removePromptFromLocalTree(promptIdToRemove: string, parentId?: string): boolean {
        const findAndRemove = (prompts: ManagedPrompt[], targetId: string, directParentId?: string): ManagedPrompt[] => {
            return prompts.filter(p => {
                if (p.id === targetId && (directParentId ? p.parentId === directParentId : !p.parentId /* for top-level */)) {
                    return false; // Remove this one
                }
                if (p.subPrompts && p.subPrompts.length > 0) {
                    p.subPrompts = findAndRemove(p.subPrompts, targetId, p.id); // Check children, pass current p.id as their parentId for matching
                }
                return true;
            });
        };

        if (parentId) { // Deleting a sub-prompt
            const parent = this.findPromptInTree(this.managedPrompts, parentId);
            if (parent && parent.subPrompts) {
                const oldLength = parent.subPrompts.length;
                parent.subPrompts = parent.subPrompts.filter(sp => sp.id !== promptIdToRemove);
                return parent.subPrompts.length < oldLength;
            }
        } else { // Deleting a top-level prompt
            const oldLength = this.managedPrompts.length;
            this.managedPrompts = this.managedPrompts.filter(p => p.id !== promptIdToRemove);
            return this.managedPrompts.length < oldLength;
        }
        return false; // Not found or not removed
    }

    private findPromptInTree(prompts: ManagedPrompt[], promptId: string): ManagedPrompt | null {
        for (const prompt of prompts) {
            if (prompt.id === promptId) return prompt;
            if (prompt.subPrompts) {
                const foundInSub = this.findPromptInTree(prompt.subPrompts, promptId);
                if (foundInSub) return foundInSub;
            }
        }
        return null;
    }

    // NEW HELPER: Gets the root document ID and the path of IDs to the targetPrompt
    private getPromptContext(targetPrompt: ManagedPrompt): { rootDocId: string, pathIdsToPrompt: string[] } | null {
        const path: ManagedPrompt[] = [];
        let current: ManagedPrompt | null = targetPrompt;

        // Traverse up to the root, collecting prompts in path from target up to root
        while (current) {
            path.push(current);
            if (!current.parentId) { // Found the root
                break;
            }
            // Find the parent in the locally managedPrompts tree
            // This assumes managedPrompts is correctly structured and targetPrompt is part of it.
            const parentInTree = this.findPromptInTree(this.managedPrompts, current.parentId);
            if (!parentInTree) {
                console.error(`Inconsistent local tree: Parent ${current.parentId} not found for prompt ${current.id} during context lookup.`);
                return null; // Critical inconsistency
            }
            current = parentInTree;
        }

        if (!current || current.parentId) { // Should have ended at a root node (no parentId)
            console.error(`Could not determine root for prompt ${targetPrompt.id}. Last processed: ${current?.id}`);
            return null;
        }

        // Path was built from target up to root. Reverse it to get path from root down to target.
        const pathFromRoot = path.reverse();
        const rootDocId = pathFromRoot[0].id!;
        // pathIdsToPrompt are the IDs of the prompts in the path, excluding the root itself, down to the targetPrompt.
        // If targetPrompt is the root, pathIdsToPrompt will be empty.
        const pathIdsToPrompt = pathFromRoot.slice(1).map(p => p.id!);

        return { rootDocId, pathIdsToPrompt };
    }

    // NEW HELPER: Finds a prompt by path within a document and applies an update function
    private updatePromptAtPath(
        docData: ManagedPrompt,      // Root document's data
        pathIds: string[],           // Path of IDs from root's children down to the target prompt. Empty if target is root.
        updateFn: (promptToUpdate: ManagedPrompt) => void
    ): boolean {
        if (pathIds.length === 0) { // Target is the root document itself
            updateFn(docData);
            return true;
        }

        let currentLevelPrompts = docData.subPrompts;
        let targetNode: ManagedPrompt | undefined = undefined; // Initialize to undefined

        // Traverse to the target node using the path segments
        let parentForCurrentSegment: ManagedPrompt = docData;
        for (let i = 0; i < pathIds.length; i++) {
            const segmentId = pathIds[i];
            if (!parentForCurrentSegment.subPrompts) {
                console.error(`updatePromptAtPath: Path broken - subPrompts not found on parent ${parentForCurrentSegment.id} for segment ${segmentId}`);
                return false; // Path broken, subPrompts array missing where expected
            }
            targetNode = parentForCurrentSegment.subPrompts.find(p => p.id === segmentId);
            if (!targetNode) {
                console.error(`updatePromptAtPath: Path broken - segment ID ${segmentId} not found in subPrompts of ${parentForCurrentSegment.id}`);
                return false; // Path broken, specific ID not found
            }

            if (i < pathIds.length - 1) { // If not the last segment, move to the next level
                parentForCurrentSegment = targetNode; // The current target becomes parent for the next segment
            }
        }

        if (targetNode) { // If targetNode was found (it's the last segment in pathIds)
            updateFn(targetNode);
            return true;
        }
        // This line should ideally not be reached if logic is correct, 
        // as loop completion with a valid path implies targetNode is set.
        console.error("updatePromptAtPath: Target node not identified after path traversal, though path seemed valid.");
        return false;
    }

}

// Interface for Managed Prompt (local component use, expects JS Dates after loading)
export interface ManagedPrompt {
    id?: string; // Firestore document ID for main prompts. For sub-prompts, a unique ID within the parent\'s array.
    text: string;
    category: string;
    parentId?: string;    // ID of the parent Firestore document (used when editingPrompt is a sub-prompt)
    subPrompts?: ManagedPrompt[]; // Array of embedded sub-prompts
    isExpanded?: boolean; // For UI tree view state
}

// Interface for writing a new prompt to Firestore (allows FieldValue for timestamps)
interface NewPromptWriteData {
    text: string;
    category: string;
    subPrompts: Array<{
        id: string;
        text: string;
        category: string;
    }>;
} 